import { CenteredLayout, ContentHeader, Css, useComputed } from "@homebound/beam";
import { useMemo } from "react";
import { LoadingTable } from "src/components/LoadingTable";
import { useRouteParams } from "src/hooks/useRouteParams";
import { celisImpactTotals, changeEventQueryFor } from "src/routes/projects/change-events/guided/utils";
import { WorkflowFormState } from "src/routes/projects/change-events/guided/workflows/types";
import { GuidedChangeEventParams } from "src/routes/routesDef";
import { fail, queriesResult, queryResult } from "src/utils";
import {
  ReviewProjectImpact_ChangeEventLineItemFragment,
  ReviewProjectImpact_ProjectCostCodeFragment,
  ReviewProjectScheduleImpactQueryVariables,
  useReviewImpactProjectQuery,
  useReviewProjectImpactChangeEventQuery,
  useReviewProjectScheduleImpactQuery,
} from "./3.ReviewImpactStep.generated";
import { ApplyConfigurationFormInput } from "./applyConfigurationForm";
import { ProjectBudgetTable } from "./components/ProjectBudgetTable";
import { ProjectImpactCards } from "./components/ProjectImpactCards";
import { ScheduleImpactTable } from "./components/ScheduleImpactTable";
import { createProjectBudgetRows, reviewImpactBudgetTotals, scheduleImpactVariablesFor } from "./reviewImpactUtils";

type ReviewImpactStepProps = { formState: WorkflowFormState<ApplyConfigurationFormInput> };

/**
 * A read-only look at what the configuration picked on step 1 does to the project's budget, scope and schedule.
 *
 * Step 1 always saves the change event, for real with a signed contract or as a dry run without one, so the impact
 * is always the change event's own line items, i.e. the deltas the real derivation produced.
 */
export function ReviewImpactStep(props: ReviewImpactStepProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<GuidedChangeEventParams>();
  const { changeEventId, fetchPolicy } = useComputed(() => changeEventQueryFor(formState), [formState]);
  const savedChangeEventId = useComputed(() => formState.id.value, [formState]);
  const targetConfigId = useComputed(() => formState.targetConfigId.value, [formState]);

  const projectQuery = useReviewImpactProjectQuery({ variables: { projectId } });
  const changeEventQuery = useReviewProjectImpactChangeEventQuery({
    variables: { changeEventId: changeEventId ?? fail("No change event id") },
    fetchPolicy,
  });
  const scheduleImpactVariables = scheduleImpactVariablesFor(projectId, savedChangeEventId, targetConfigId);

  return queriesResult([projectQuery, changeEventQuery] as const, {
    data: (projectData, changeEventData) => {
      const { project, projectItemsPage } = projectData;
      const projectItemsCount = projectItemsPage.pageInfo.totalCount;
      const { lineItems } = changeEventData.changeEvent;
      return (
        <ReviewImpactView
          projectCostCodes={project.projectCostCodes}
          lineItems={lineItems}
          currentItemCount={projectItemsCount}
          newItemCount={projectItemsCount + celisImpactTotals(lineItems).netItems}
          scheduleImpactVariables={scheduleImpactVariables}
        />
      );
    },
  });
}

type ReviewImpactViewProps = {
  projectCostCodes: ReviewProjectImpact_ProjectCostCodeFragment[];
  lineItems: ReviewProjectImpact_ChangeEventLineItemFragment[];
  currentItemCount: number;
  newItemCount: number;
  scheduleImpactVariables: ReviewProjectScheduleImpactQueryVariables;
};

function ReviewImpactView(props: ReviewImpactViewProps) {
  const { projectCostCodes, lineItems, currentItemCount, newItemCount, scheduleImpactVariables } = props;

  /**
   * Deliberately not in the step's `queriesResult`: projecting the schedule re-runs the simulator, so the budget
   * table would sit behind a 30s-timeout HTTP call for a number it doesn't need. The card and the table skeleton
   * themselves instead.
   */
  const scheduleImpactQuery = useReviewProjectScheduleImpactQuery({ variables: scheduleImpactVariables });

  const rows = useMemo(
    () =>
      createProjectBudgetRows(
        projectCostCodes,
        lineItems.map((celi) => ({
          costCode: celi.item.costCode,
          totalCostInCents: celi.totalCostInCents,
          totalPriceInCents: celi.totalPriceInCents,
        })),
      ),
    [projectCostCodes, lineItems],
  );

  // The table shows each cost code's change, so the cards are the only place that needs the resulting budget
  const totals = reviewImpactBudgetTotals(projectCostCodes, rows);

  return (
    <CenteredLayout size="lg">
      <div css={Css.df.fdc.py3.gap8.$}>
        <div css={Css.df.fdc.gap2.$}>
          <ContentHeader
            title="Review Project Impact"
            description="Review the resulting budget, scope, and schedule before applying this configuration."
          />
          <ProjectImpactCards
            totals={totals}
            currentItemCount={currentItemCount}
            newItemCount={newItemCount}
            scheduleImpact={scheduleImpactQuery.data?.projectScheduleImpact}
          />
        </div>

        <div css={Css.df.fdc.gap2.$}>
          <ContentHeader title="Project Budget" description="Review the resulting budget by cost code." />
          <ProjectBudgetTable budgetRows={rows} />
        </div>

        <div css={Css.df.fdc.gap2.$}>
          <ContentHeader
            title="Schedule Impact"
            description="How this configuration shifts the project's milestones."
          />
          {queryResult(scheduleImpactQuery, {
            loading: () => <LoadingTable columns={4} />,
            data: ({ projectScheduleImpact }) => <ScheduleImpactTable milestones={projectScheduleImpact.milestones} />,
          })}
        </div>
      </div>
    </CenteredLayout>
  );
}
